import { Grid, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import React from 'react';
import PageMetaHead from 'src/logic/config/meta/PageMetaHead';
import useScrollObserver from 'src/logic/hooks/use-scroll-observer';
import { BlogPost } from 'src/logic/models/blog';
import { PaginatedResponse } from 'src/logic/models/queries';
import { getAllBlogPosts } from 'src/logic/services/blog';
import EmptySection from 'src/ui/components/smart/catalogue/catalogue-content/components/EmptySection';
import ErrorSection from 'src/ui/components/smart/catalogue/catalogue-content/components/ErrorSection';
import LoadingSection from 'src/ui/components/smart/catalogue/catalogue-content/components/LoadingSection';
import BlogPostCard from 'src/ui/components/smart/landing/blog/BlogPostCard';
import LandingLayout from 'src/ui/components/smart/landing/layout/LandingLayout';

const PER_PAGE = 12;

type IProps = {
  initialData: PaginatedResponse<BlogPost> | null;
};

const BlogPage = ({ initialData }: IProps) => {
  const { data, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['BLOG_POSTS'],
    queryFn: async ({ pageParam = initialData ? 2 : 1 }) => {
      const { data } = await getAllBlogPosts(pageParam, PER_PAGE);
      return data.data;
    },
    getNextPageParam: lastPage => {
      const { current_page, last_page } = lastPage?.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialData: initialData
      ? {
          pageParams: [1],
          pages: [initialData],
        }
      : undefined,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 1,
  });
  const posts = data?.pages?.flatMap(page => page?.data ?? []) ?? [];
  const isEmpty = posts.length === 0;

  const { observerRef, containerRef } = useScrollObserver({
    onIntersect: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage?.();
      }
    },
  });

  if (isLoading) {
    return <LoadingSection label='Loading Posts...' minHeight={'85vh'} />;
  }
  if (isError) {
    return <ErrorSection refetch={refetch} minHeight='85vh' />;
  }
  if (isEmpty) {
    return <EmptySection hideQuestion text='No posts found' minHeight='85vh' />;
  }
  return (
    <div className='page' ref={containerRef}>
      <div className='page__content' style={{ paddingBlock: '4em' }}>
        <Typography
          color='#3A3E50'
          fontWeight={600}
          fontSize={{ xs: 26, lg: 44 }}
          textAlign={'center'}
          mb={4}
          mx={'auto'}
          maxWidth={'15ch'}
          lineHeight={1}
          component='h1'
        >
          Stay Updated With <span style={{ color: '#005FCC' }}>Our Latest Posts</span>
        </Typography>

        <Grid container spacing={2}>
          {posts.map(post => (
            <Grid key={`${post.id}-${post.slug}`} item xs={12} sm={6} md={4} lg={4} xl={4}>
              <BlogPostCard post={post} />
            </Grid>
          ))}
        </Grid>

        {hasNextPage && (
          <div
            style={{ marginBlock: '2rem', opacity: isFetchingNextPage ? 1 : 0, transition: 'opacity 200ms' }}
            ref={observerRef}
          >
            <LoadingSection minHeight='auto' label='Loading More...' loadingIndicatorSize='1rem' />
          </div>
        )}
      </div>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async () => {
  let initialData: PaginatedResponse<BlogPost> | null = null;

  try {
    const response = await getAllBlogPosts(1, PER_PAGE);
    initialData = response.data.data;
  } catch (err) {
    console.log(err);
  }

  return {
    props: { initialData },
  };
};

BlogPage.getLayout = function getLayout(page: React.ReactElement) {
  return (
    <LandingLayout>
      <PageMetaHead
        page_meta={{
          title: 'AI-Powered Procurement Solutions for Modern Businesses',
          description:
            'Streamline your procurement process with AI-driven solutions, Automate tendering, sourcing, contract approvals, and supplier management using smart SaaS software.',
        }}
      />
      {page}
    </LandingLayout>
  );
};

export default BlogPage;
